import type { Level } from "./types";

export interface GridModel {
  dates: string[];
  minutes: number[];
  slotAt(r: number, c: number): number | null;
}

/** A rebuild key for a grid model: the dates, the minute labels, AND every `slotAt` value. Two
 * models can share the same dates and minute labels while every cell actually stands for a
 * different instant (the organizer changed timezone but kept the same civil dates and window), in
 * which case a key built from labels alone would not change and a rebuild would be skipped when
 * it should not be. */
export function gridKey(model: GridModel): string {
  let key = `${model.dates.join(",")}|${model.minutes.join(",")}`;
  for (let r = 0; r < model.minutes.length; r++) {
    for (let c = 0; c < model.dates.length; c++) {
      key += `|${model.slotAt(r, c)}`;
    }
  }
  return key;
}

export interface Drag {
  base: Record<string, Level>;
  r0: number;
  c0: number;
  level: Level;
}

export function beginDrag(
  current: Record<string, Level>,
  model: GridModel,
  r: number,
  c: number,
  brush: Level,
): Drag {
  const slot = model.slotAt(r, c);
  const existing = slot === null ? undefined : current[String(slot)];
  const erase = brush !== 0 && existing === brush;
  return {
    base: { ...current },
    r0: r,
    c0: c,
    level: erase ? 0 : brush,
  };
}

export function dragTo(drag: Drag, model: GridModel, r: number, c: number): Record<string, Level> {
  const next: Record<string, Level> = { ...drag.base };
  const rMin = Math.min(drag.r0, r);
  const rMax = Math.max(drag.r0, r);
  const cMin = Math.min(drag.c0, c);
  const cMax = Math.max(drag.c0, c);
  for (let rr = rMin; rr <= rMax; rr++) {
    for (let cc = cMin; cc <= cMax; cc++) {
      const slot = model.slotAt(rr, cc);
      if (slot === null) continue;
      const key = String(slot);
      if (drag.level === 0) delete next[key];
      else next[key] = drag.level;
    }
  }
  return next;
}

export interface GridEdges {
  colLeft: number[];
  colRight: number[];
  rowTop: number[];
  rowBottom: number[];
}

export function cellAt(edges: GridEdges, x: number, y: number): { r: number; c: number } | null {
  if (edges.colLeft.length === 0 || edges.rowTop.length === 0) return null;
  return {
    r: axisIndex(edges.rowTop, edges.rowBottom, y),
    c: axisIndex(edges.colLeft, edges.colRight, x),
  };
}

function axisIndex(starts: number[], ends: number[], v: number): number {
  const n = starts.length;
  const first = starts[0];
  const lastEnd = ends[n - 1];
  if (first === undefined || lastEnd === undefined) return 0;
  if (v < first) return 0;
  if (v >= lastEnd) return n - 1;
  for (let i = 0; i < n; i++) {
    const a = starts[i];
    const b = ends[i];
    if (a === undefined || b === undefined) continue;
    if (v >= a && v < b) return i;
  }
  let best = 0;
  let bestDist = Infinity;
  for (let i = 0; i < n; i++) {
    const a = starts[i];
    const b = ends[i];
    if (a === undefined || b === undefined) continue;
    const dist = v < a ? a - v : v > b ? v - b : 0;
    if (dist < bestDist) {
      bestDist = dist;
      best = i;
    }
  }
  return best;
}

export function diffCells(a: Record<string, Level>, b: Record<string, Level>): { slot: string; level: Level }[] {
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
  const out: { slot: string; level: Level }[] = [];
  for (const slot of keys) {
    const left = a[slot] ?? 0;
    const right = b[slot] ?? 0;
    if (left !== right) out.push({ slot, level: right });
  }
  return out;
}
